import type { SavedConfig } from "@cfg/api-contract";
import { deleteDraft, listDrafts, markAdopted, type Draft, type FitTagValue } from "@cfg/configurator-ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useApp } from "../app-context.tsx";
import { useAuthUser } from "../auth.ts";
import { useMe } from "../me.ts";
import { DefaultEmptyState } from "./defaults.tsx";
import { DisplayNameForm } from "./DisplayNameForm.tsx";

interface Item {
  id: string;
  name: string;
  designId: string;
  version: string;
  updatedAt: string;
  kind: "draft" | "config";
  fits: FitTagValue[];
  printed: boolean;
  isPublic: boolean;
}

/** Saved configurations: local drafts (anonymous) plus profile configs (signed in). */
export function MinePage() {
  const { brand, copy, api, auth } = useApp();
  const user = useAuthUser(auth);
  const qc = useQueryClient();
  const Empty = brand.slots?.EmptyState ?? DefaultEmptyState;
  const me = useMe();
  const [fitFilter, setFitFilter] = useState("");
  const [editingName, setEditingName] = useState(false);

  const drafts = useQuery({ queryKey: ["drafts", brand.config.id], queryFn: () => listDrafts(brand.config.id) });
  const configs = useQuery({
    queryKey: ["configs", user?.sub],
    queryFn: async () => (api && user ? (await api.listConfigs()).configs : []),
    enabled: !!(api && user),
  });

  // Adopt anonymous drafts into the profile on first sign-in.
  useEffect(() => {
    if (!api || !user || !drafts.data?.length) return;
    let cancelled = false;
    (async () => {
      for (const d of drafts.data) {
        if (cancelled) return;
        try {
          const cfg = await api.createConfig({ name: d.name, designId: d.designId, version: d.version, params: d.params, fits: d.fits, printed: d.printed, notes: d.notes });
          await markAdopted(d.id, cfg.id);
        } catch (err) {
          console.warn("Could not adopt draft", d.id, err);
        }
      }
      await qc.invalidateQueries({ queryKey: ["drafts"] });
      await qc.invalidateQueries({ queryKey: ["configs"] });
    })();
    return () => {
      cancelled = true;
    };
  }, [api, user, drafts.data, qc]);

  const all: Item[] = [
    ...(configs.data ?? []).map((c: SavedConfig): Item => ({ ...c, kind: "config", isPublic: c.visibility === "public" })),
    ...(drafts.data ?? []).map((d: Draft): Item => ({ ...d, kind: "draft", fits: d.fits ?? [], printed: d.printed ?? false, isPublic: false })),
  ].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  const filter = fitFilter.trim().toLowerCase().replace(/\s+/g, "");
  const items = filter ? all.filter((it) => it.fits.some((f) => f.ref.includes(filter) || f.name?.toLowerCase().includes(filter))) : all;
  const showFits = brand.config.features.fitTags && all.some((it) => it.fits.length > 0);
  const displayName = me.data?.displayName;

  const remove = async (item: Item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    if (item.kind === "draft") await deleteDraft(item.id);
    else if (api) await api.deleteConfig(item.id);
    await qc.invalidateQueries({ queryKey: [item.kind === "draft" ? "drafts" : "configs"] });
  };

  return (
    <section className="page page-mine">
      <header className="page-head">
        <h1>{copy("mine.title")}</h1>
        {!user && auth.enabled && (
          <p className="page-sub">
            {copy("auth.required")}{" "}
            <button type="button" className="link" onClick={() => void auth.signIn()}>
              {copy("auth.signIn")}
            </button>
          </p>
        )}
        {brand.config.features.publicGallery && user && api && (
          <div className="mine-profile">
            {displayName && !editingName ? (
              <p className="muted small">
                {copy("profile.displayName")}: <strong>{displayName}</strong>{" "}
                <button type="button" className="link" onClick={() => setEditingName(true)}>
                  change
                </button>
              </p>
            ) : (
              (editingName || me.isSuccess) && <DisplayNameForm initial={displayName} onDone={() => setEditingName(false)} />
            )}
          </div>
        )}
        {showFits && (
          <input className="cfg-input mine-filter" placeholder={`${copy("community.search")}…`} value={fitFilter} onChange={(e) => setFitFilter(e.target.value)} />
        )}
      </header>
      {items.length === 0 ? (
        <Empty title={copy("mine.empty")} />
      ) : (
        <ul className="mine-list">
          {items.map((it) => (
            <li key={`${it.kind}:${it.id}`} className="mine-item">
              <a href={`/design/${encodeURIComponent(it.designId)}?c=${encodeURIComponent(it.id)}`}>
                <strong>{it.name}</strong>
                <span className="muted small">
                  {it.designId} · v{it.version} · {new Date(it.updatedAt).toLocaleString()} {it.kind === "draft" ? "· on this device" : ""}
                </span>
                {(it.fits.length > 0 || it.printed || it.isPublic) && (
                  <span className="mine-tags">
                    {it.fits.map((f) => (
                      <span key={f.ref} className="cfg-fit-chip" title={copy("fits.chip", { label: brand.config.fitTags.label, ref: f.ref })}>
                        <strong>{f.ref}</strong>
                        {f.name && <span>{f.name}</span>}
                      </span>
                    ))}
                    {it.printed && <span className="version-pill">{copy("community.printed")}</span>}
                    {it.isPublic && <span className="version-pill">{copy("community.public")}</span>}
                  </span>
                )}
              </a>
              <button type="button" className="cfg-chip" onClick={() => remove(it)}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
